import {
  Alert,
  Button,
  Card,
  Empty,
  Skeleton,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { useFetch } from "../../hooks/useFetch";
import { DiagnosticoOperacionalIrregularidade } from "../../interfaces/DiagnosticoOperacionalIrregularidade";
import { buscarDiagnosticoOperacionalIrregularidadesService } from "../../services/buscarDiagnosticoOperacionalIrregularidadesService";
import { Container, Description, Header, Title } from "../ViewDashboardOperacional/styles";
import AjustarCotaModal from "./AjustarCotaModal";

const irregularidadeColorMap: Record<string, string> = {
  EP: "blue",
  AP: "gold",
  PEU: "red",
};

const resolveIrregularidadePrincipal = (
  irregularidade: Partial<DiagnosticoOperacionalIrregularidade> | null | undefined
) => {
  if (irregularidade?.irregularidadePrincipal) {
    return irregularidade.irregularidadePrincipal;
  }

  const irregularidadesRaw = irregularidade?.irregularidades;
  const irregularidades = Array.isArray(irregularidadesRaw)
    ? irregularidadesRaw.filter(
        (item): item is string => typeof item === "string"
      )
    : [];

  const prioridade = ["PEU", "EP", "AP"];
  const irregularidadePrioritaria = prioridade.find((item) =>
    irregularidades.includes(item)
  );

  return irregularidadePrioritaria ?? irregularidades[0] ?? "";
};

export default function ViewDashboardOperacionalIrregularidades() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [messageApi, contextHolder] = message.useMessage();
  const [irregularidadeSelecionada, setIrregularidadeSelecionada] =
    useState<DiagnosticoOperacionalIrregularidade | null>(null);

  const {
    data: irregularidades,
    isLoading,
    error,
  } = useFetch(
    true,
    buscarDiagnosticoOperacionalIrregularidadesService,
    [refreshKey]
  );

  const abrirModalAjuste = (record: DiagnosticoOperacionalIrregularidade) => {
    setIrregularidadeSelecionada({
      ...record,
      irregularidadePrincipal: resolveIrregularidadePrincipal(record),
    });
  };

  const fecharModalAjuste = () => {
    setIrregularidadeSelecionada(null);
  };

  const handleAjusteSuccess = () => {
    fecharModalAjuste();
    messageApi.success("CTU ajustada com sucesso.");
    setRefreshKey((currentValue) => currentValue + 1);
  };

  const usuarioFilters =
    irregularidades?.map((item) => item.nomeUsuario).filter(Boolean) ?? [];
  const empreendimentoFilters =
    irregularidades?.map((item) => item.nomeEmpreendimento).filter(Boolean) ??
    [];
  const irregularidadeAjustavel = (value: string) =>
    value === "EP" || value === "AP" || value === "PEU";

  const columns: ColumnsType<DiagnosticoOperacionalIrregularidade> = [
    {
      title: "Usuario",
      dataIndex: "nomeUsuario",
      key: "nomeUsuario",
      width: 220,
      filters: Array.from(new Set(usuarioFilters))
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({
          text: value,
          value,
        })),
      filterSearch: true,
      onFilter: (value, record) => record.nomeUsuario === value,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span>{record.nomeUsuario}</span>
          <Typography.Text type="secondary">
            ID usuario: {record.usuarioId}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: "Empreendimento",
      dataIndex: "nomeEmpreendimento",
      key: "nomeEmpreendimento",
      width: 220,
      filters: Array.from(new Set(empreendimentoFilters))
        .sort((a, b) => a.localeCompare(b))
        .map((value) => ({
          text: value,
          value,
        })),
      filterSearch: true,
      onFilter: (value, record) => record.nomeEmpreendimento === value,
    },
    {
      title: "Apartamento",
      dataIndex: "nomeApto",
      key: "nomeApto",
      width: 180,
    },
    {
      title: "Cota",
      dataIndex: "numeroCota",
      key: "numeroCota",
      width: 90,
    },
    {
      title: "Irregularidade principal",
      dataIndex: "irregularidadePrincipal",
      key: "irregularidadePrincipal",
      width: 180,
      render: (_, record) => {
        const value = resolveIrregularidadePrincipal(record);

        return (
          <Tag color={irregularidadeColorMap[value] || "default"}>
            {value || "-"}
          </Tag>
        );
      },
    },
    {
      title: "Irregularidades",
      dataIndex: "irregularidades",
      key: "irregularidades",
      width: 220,
      render: (values: string[]) => (
        <>
          {values.map((value, index) => (
            <Tag
              key={`${value}-${index}`}
              color={irregularidadeColorMap[value] || "default"}
            >
              {value}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: "Acao",
      key: "acao",
      width: 120,
      fixed: "right",
      render: (_, record) =>
        irregularidadeAjustavel(resolveIrregularidadePrincipal(record)) ? (
          <Button type="link" onClick={() => abrirModalAjuste(record)}>
            Ajustar
          </Button>
        ) : (
          <Typography.Text type="secondary">-</Typography.Text>
        ),
    },
  ];
  console.log("irregularidadeSelecionada:", irregularidadeSelecionada);

  return (
    <Container>
      {contextHolder}
      <Header>
        <Title>CTUs com problema</Title>
        <Description>
          Lista das cotas usuarios com irregularidades identificadas no dashboard
          operacional.
        </Description>
      </Header>

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 10 }} />
      ) : error ? (
        <Alert
          type="error"
          showIcon
          message="Nao foi possivel carregar a lista de CTUs com problema."
          description="Verifique a conexao com a API e tente novamente."
        />
      ) : !irregularidades ? (
        <Alert
          type="warning"
          showIcon
          message="Nenhuma resposta foi retornada para a lista de irregularidades."
        />
      ) : irregularidades.length === 0 ? (
        <Card>
          <Empty description="Nenhuma CTU com problema encontrada." />
        </Card>
      ) : (
        <Card
          title={`Total de CTUs com problema: ${irregularidades.length}`}
          bodyStyle={{ padding: 0 }}
        >
          <Table<DiagnosticoOperacionalIrregularidade>
            rowKey="cotaUsuarioId"
            columns={columns}
            dataSource={irregularidades}
            scroll={{ x: 1320 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50"],
            }}
          />
        </Card>
      )}

      

      {irregularidadeSelecionada ? (
        <AjustarCotaModal
          open
          irregularidade={irregularidadeSelecionada}
          onCancel={fecharModalAjuste}
          onSuccess={handleAjusteSuccess}
        />
      ) : null}
    </Container>
  );
}
